/**
 * The one display format for times across the apps: `DD/MM HH:mm:ss UTC` or
 * `DD/MM HH:mm:ss LT` (the viewer's local time).
 *
 * Strings without a zone (`2026-11-03T02:25:00`, `2026-11-03 02:25`) are read
 * as UTC, the way the backend sends them. Anything that cannot be read renders
 * the placeholder instead of `NaN`.
 */

export type TTimeZoneMode = 'utc' | 'local';

export type TDateInput = string | number | Date | null | undefined;

export interface IFormatDateTimeOptions {
  /** Shown for an empty or unreadable value. Default `—`. */
  placeholder?: string;
  /** Append `.SSS` after the seconds, for millisecond traces. */
  milliseconds?: boolean;
}

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/** A value as a `Date`, zone-less strings read as UTC; `null` when unreadable. */
export const toDate = (value: TDateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const text = String(value).trim();
  if (!text) return null;
  const iso =
    text.length >= 16 && !HAS_ZONE.test(text)
      ? `${text.replace(' ', 'T')}Z`
      : text;
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) return d;
  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** `04/11 10:42:05 UTC` or `04/11 17:42:05 LT`. */
export const formatDateTime = (
  value: TDateInput,
  mode: TTimeZoneMode = 'utc',
  {placeholder = '—', milliseconds = false}: IFormatDateTimeOptions = {}
): string => {
  const d = toDate(value);
  if (!d) return placeholder;
  const utc = mode === 'utc';
  const day = utc ? d.getUTCDate() : d.getDate();
  const month = (utc ? d.getUTCMonth() : d.getMonth()) + 1;
  const hours = utc ? d.getUTCHours() : d.getHours();
  const minutes = utc ? d.getUTCMinutes() : d.getMinutes();
  const seconds = utc ? d.getUTCSeconds() : d.getSeconds();
  const ms = milliseconds
    ? `.${pad(utc ? d.getUTCMilliseconds() : d.getMilliseconds(), 3)}`
    : '';
  return `${pad(day)}/${pad(month)} ${pad(hours)}:${pad(minutes)}:${pad(
    seconds
  )}${ms} ${utc ? 'UTC' : 'LT'}`;
};

/** `04/11 10:42:05 UTC` */
export const formatUtc = (
  value: TDateInput,
  options?: IFormatDateTimeOptions
): string => formatDateTime(value, 'utc', options);

/** `04/11 17:42:05 LT` */
export const formatLocal = (
  value: TDateInput,
  options?: IFormatDateTimeOptions
): string => formatDateTime(value, 'local', options);
